import {
	AlertManager,
	useAlerts,
	useSynchronizedRef,
	useValueEqualMemo,
	useFileSelector,
	useSetStateAction,
	useStableImperativeCallback
} from "client/Hooks";
import {
	Dispatch,
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import {
	DraftEmailAttachment,
	DraftEmailModel,
	NewDraftEmailModel,
	SendOrSaveEmailDraftModel
} from "client/Api/Email/respondToEmail";
import { Body, BodyHandle } from "./Body/Body";
import { Header, HeaderHandle } from "./Header/Header";
import { DraftAutoSaver } from "./draftAutoSaver";
import { WebDavConfiguration, userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	EmailImportance,
	ExchangeEmailId,
	ExchangeFolderId,
	ExchangeMessageBodyKind,
	MailMessageLocationModel,
	MailMessageLocationQueryModel
} from "client/Api/Email/common";
import {
	deleteAttachment,
	EmailAddressModel,
	getDraftById,
	sendEmail,
	SignatureModel
} from "client/Api/Email/email";
import {
	CreateNewEmailSessionState,
	EditDraftEmailSessionState,
	EmailResponseSessionState,
	EmailSessionStateKind
} from "../../EmailPage/EmailPage";
import {
	isNil,
	isNotNil,
	isNotNilOrEmpty,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { never } from "client_legacy/ndaw/errors";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./EmailComposer.module.less";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { HttpRequestError } from "client/Api/httpClient";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { useSignatureStore } from "../../Signatures/signatureStore";
import {
	useUploadAttachmentsFromDevice,
	useUploadAttachmentsFromDms,
	useUploadInlineImage
} from "./AttachmentManager";
import {
	AttachmentItem,
	AttachmentStatus,
	convertToAttachmentItem,
	EnqueueAttachmentUpload,
	UploadedInlineAttachment
} from "./AttachmentManager/common";
import { inline, IValidationContext } from "client/Validation/rules";
import { IValidationRuleWithBuilder } from "client/Validation/rules/triggers";
import { configureValidationStep, IObjectValidationStepBuilder } from "client/Validation/steps";
import { FontStyleModel } from "client/Api/common";
import { CancelEmailComposeDialog } from "../CancelEmailComposeDialog";
import { useSelectFilesDialog } from "client/Features/Documents/Dialogs/SelectFilesDialog";
import { FileSelection, ItemPickerMode } from "client/Features/Documents/ItemPicker/ItemPicker";
import { isValidRecipientValue } from "./validation";
import { convertPlainTextBodyToHtml } from "../../utils";

export type EmailComposerProps = {
	sessionState: CreateNewEmailSessionState | EditDraftEmailSessionState;
	smtpAddress: string | null;
	fromAddressOptions: readonly EmailAddressModel[];
	defaultComposerFontStyle: FontStyleModel;
	onEmailSent(emailId: ExchangeEmailId, response: EmailResponseSessionState | null): void;
	onDeleteEmail(
		emailId: ExchangeEmailId,
		parentFolderId: string | null
	): void;
	onDraftSaved: () => void;
	onHasUploadingAttachmentsChange(hasPendingAttachments: boolean): void;
	onIsBusyChange: (value: boolean) => void;
};

export type EmailComposerHandle = {
	importance: EmailImportance | null;
	setImportance(importance: EmailImportance): void;
	send(): void;
	cancel(): void;
	insertSignature(signature: string): void;
	attachFilesFromLocalDevice(): void;
	attachFilesFromDms(options: AttachFromDmsOptions): void;
	insertInlineImage(): void;
};

export const EmailComposer = forwardRef(
	(props: EmailComposerProps, ref: Ref<EmailComposerHandle>): ReactElement | null => {
		const {
			defaultComposerFontStyle,
			sessionState,
			smtpAddress,
			fromAddressOptions,
			onEmailSent,
			onDeleteEmail,
			onDraftSaved,
			onHasUploadingAttachmentsChange,
			onIsBusyChange: onIsBusyChangeFromProps
		} = props;

		const loadedDraftRef: RefObject<LoadedDraftHandle | null> = useRef<LoadedDraftHandle>(null);
		const alertManager: AlertManager = useAlerts();
		const [draftFormData, setDraftFormData] = useState<EmailDraftFormData>();
		const [originalHead, setOriginalHead] = useState<string>("");
		const [sessionId, setSessionId] = useState<Guid>();
		const [attachmentState, setAttachmentState] = useState<AttachmentState>({
			items: [],
			totalSize: 0
		});
		const pendingCommandsRef: ReadonlyRefObject<EmailSessionCommand[]> = useRef<EmailSessionCommand[]>([]);

		const defaultFromAddressRef: ReadonlyRefObject<string> = useSynchronizedRef(
			(
				fromAddressOptions.find(f => f.isDefault)
					?? fromAddressOptions[0]
			)?.emailAddress
				?? ""
		);

		const onIsBusyChange: (value: boolean) => void = useStableImperativeCallback(onIsBusyChangeFromProps);

		const totalVirtualSize = useMemo(
			(): number => {
				const sizeOfPendingUploads = attachmentState.items
					.filter(x => x.status === AttachmentStatus.Uploading)
					.reduce(
						(acc, cur) => acc + cur.size,
						0
					);

				return attachmentState.totalSize + sizeOfPendingUploads;
			},
			[attachmentState]
		);

		const sizeLimit = useMemo(
			(): number => {
				return userConfiguration().exchange
					.totalAttachmentsSizeLimit;
			},
			[]
		);

		const lastAttachmentUploadRef: RefObject<Promise<void>> = useRef<Promise<void>>(COMPLETED_UPLOAD);

		const enqueueAttachmentUpload: EnqueueAttachmentUpload = useCallback(
			<T,>(operation: () => Promise<T>): Promise<T> => {
				const result: Promise<T> = lastAttachmentUploadRef.current
					.then(operation);

				lastAttachmentUploadRef.current = result.then(
					(): void => undefined,
					(): void => undefined
				);

				return result;
			},
			[]
		);

		const addPendingCommandHandler = useCallback(
			(action: EmailSessionCommand): void => {
				pendingCommandsRef.current
					.push(action);
			},
			[]
		);

		const removePendingCommandHandler = useCallback(
			(commandId: Guid): void => {
				const commandIndex = pendingCommandsRef.current
					.findIndex(c => c.commandId === commandId);

				if (commandIndex !== -1) {
					pendingCommandsRef.current
						.splice(commandIndex, 1);
				}
			},
			[]
		);

		const getPendingCommands = useCallback(
			(): readonly EmailSessionCommand[] => {
				return [...pendingCommandsRef.current];
			},
			[]
		);

		useComposeNewEmail(
			sessionState,
			smtpAddress,
			defaultFromAddressRef,
			setDraftFormData,
			onIsBusyChange,
			setAttachmentState,
			setSessionId,
			addPendingCommandHandler
		);

		useEditDraft(
			sessionState,
			defaultFromAddressRef,
			setDraftFormData,
			onIsBusyChange,
			setAttachmentState,
			setSessionId,
			addPendingCommandHandler,
			setOriginalHead
		);

		useEffect(
			(): void => {
				onHasUploadingAttachmentsChange(
					attachmentState.items
						.some(i => i.status === AttachmentStatus.Uploading)
				);
			},
			[attachmentState.items, onHasUploadingAttachmentsChange]
		);

		const addAttachmentHandler = useCallback(
			(attachment: AttachmentItem): void => {
				setAttachmentState(attachmentState => ({
					items: [
						...attachmentState.items,
						attachment
					],
					totalSize: attachmentState.totalSize + (
						attachment.status === AttachmentStatus.Uploaded
							? attachment.size
							: 0
					)
				}));
			},
			[]
		);

		const attachmentUploadFinishHandler = useCallback(
			(
				uid: string,
				model: DraftEmailAttachment
			): void => {
				setAttachmentState(
					attachmentState => {
						const itemsCopy: AttachmentItem[] = [...attachmentState.items];
						const attachmentIndex: number = itemsCopy.findIndex(a => a.uid === uid);

						if (attachmentIndex === -1) {
							console.error("Cannot change attachment's status, because it does not exist.");
							return attachmentState;
						}

						const existingItem: AttachmentItem = itemsCopy[attachmentIndex];
						if (existingItem.status === AttachmentStatus.Uploaded) {
							console.error("Item is already in uploaded state");
							return attachmentState;
						}

						itemsCopy[attachmentIndex] = convertToAttachmentItem(model, uid);

						return {
							items: itemsCopy,
							totalSize: attachmentState.totalSize + model.size
						};
					}
				);
			},
			[]
		);

		const removeAttachmentLocallyHandler = useCallback(
			(uid: string): void => {
				setAttachmentState(attachmentState => {
					const itemsCopy: AttachmentItem[] = [...attachmentState.items];
					const attachmentIndex: number = itemsCopy.findIndex(a => a.uid === uid);

					if (attachmentIndex === -1) {
						console.error("Cannot remove attachment because it does not exist.");
						return attachmentState;
					}

					const [existingItem] = itemsCopy.splice(
						attachmentIndex,
						1
					);

					return {
						items: itemsCopy,
						totalSize: attachmentState.totalSize - (
							existingItem.status === AttachmentStatus.Uploaded
								? existingItem.size
								: 0
						)
					};
				});
			},
			[]
		);

		const deleteAttachmentHandler = useCallback(
			async (uid: string): Promise<void> => {
				const attachmentToDelete = attachmentState.items
					.find(a => a.uid === uid);

				if (!draftFormData) {
					throw new Error("Cannot delete attachment from not loaded draft");
				}

				if (!attachmentToDelete || attachmentToDelete.status !== AttachmentStatus.Uploaded) {
					throw new Error("Attachment does not exist on the server");
				}

				await deleteAttachment(
					{
						message: {
							smtpAddress: draftFormData.smtpAddress,
							messageId: draftFormData.exchangeId.id
						},
						exchangeAttachmentId: attachmentToDelete.exchangeId
					}
				);

				removeAttachmentLocallyHandler(uid);
			},
			[
				attachmentState.items,
				draftFormData,
				removeAttachmentLocallyHandler
			]
		);

		useImperativeHandle(
			ref,
			(): EmailComposerHandle => {
				const ensureLoadedDraft = (handle: LoadedDraftHandle | null): handle is LoadedDraftHandle => {
					if (!handle) {
						alertManager.showError(localize("_EmailSendFailed"));
						console.error("Unable to send not loaded draft");
						return false;
					}

					return true;
				};

				return {
					importance: draftFormData?.importance ?? null,
					setImportance(importance: EmailImportance): void {
						setDraftFormData(
							formData => {
								if (!formData) {
									return formData;
								}

								return {
									...formData,
									importance
								};
							}
						);
					},
					send(): void {
						if (ensureLoadedDraft(loadedDraftRef.current)) {
							loadedDraftRef.current
								.send();
						}
					},
					cancel(): void {
						if (ensureLoadedDraft(loadedDraftRef.current)) {
							loadedDraftRef.current
								.cancel();
						}
					},
					insertSignature(signature: string): void {
						if (ensureLoadedDraft(loadedDraftRef.current)) {
							loadedDraftRef.current
								.insertSignature(signature);
						}
					},
					attachFilesFromLocalDevice(): void {
						if (ensureLoadedDraft(loadedDraftRef.current)) {
							loadedDraftRef.current
								.attachFilesFromLocalDevice();
						}
					},
					attachFilesFromDms(options: AttachFromDmsOptions): void {
						if (ensureLoadedDraft(loadedDraftRef.current)) {
							loadedDraftRef.current
								.attachFilesFromDms(options);
						}
					},
					insertInlineImage(): void {
						if (ensureLoadedDraft(loadedDraftRef.current)) {
							loadedDraftRef.current
								.insertInlineImage();
						}
					}
				};
			},
			[alertManager, draftFormData?.importance]
		);

		if (!draftFormData) {
			return null;
		}

		return (
			<>
				{draftFormData && (
					<LoadedDraft
						ref={loadedDraftRef}
						formData={draftFormData}
						onFormDataChange={setDraftFormData}
						fromAddressOptions={fromAddressOptions}
						onIsBusyChange={onIsBusyChange}
						onEmailSent={onEmailSent}
						response={
							sessionState.kind === EmailSessionStateKind.EditDraft
								? sessionState.response ?? null
								: null
						}
						attachments={attachmentState.items}
						onAddAttachment={addAttachmentHandler}
						onAttachmentUploadFinish={attachmentUploadFinishHandler}
						onRemoveAttachmentLocally={removeAttachmentLocallyHandler}
						onDeleteAttachment={deleteAttachmentHandler}
						enqueueAttachmentUpload={enqueueAttachmentUpload}
						totalVirtualSize={totalVirtualSize}
						sizeLimit={sizeLimit}
						onDeleteEmail={onDeleteEmail}
						sessionId={sessionId}
						getPendingCommands={getPendingCommands}
						onRemovePendingCommand={removePendingCommandHandler}
						originalHead={originalHead}
						doesRepresentNewlyGeneratedDraft={
							sessionState.kind === EmailSessionStateKind.NewEmail
								|| sessionState.doesRepresentNewlyGeneratedDraft
						}
						doesContainReplies={
							sessionState.kind === EmailSessionStateKind.EditDraft
							&& isNotNil(sessionState.response)
						}
						defaultComposerFontStyle={defaultComposerFontStyle}
						onDraftSaved={onDraftSaved}
					/>
				)}
			</>
		);
	}
);
EmailComposer.displayName = "EmailComposer";

type EmailDraftFormData = SendOrSaveEmailDraftModel & {
	parentFolderId: ExchangeFolderId | null;
};

type LoadedDraftProps = {
	formData: EmailDraftFormData;
	onFormDataChange(formData: EmailDraftFormData): void;
	attachments: readonly AttachmentItem[];
	onAddAttachment(attachment: AttachmentItem): void;
	onAttachmentUploadFinish(
		uid: string,
		model: DraftEmailAttachment
	): void;
	onRemoveAttachmentLocally(uid: string): void;
	onDeleteAttachment(uid: string): void;
	enqueueAttachmentUpload: EnqueueAttachmentUpload;
	fromAddressOptions: readonly EmailAddressModel[];
	onIsBusyChange(isBusy: boolean): void;
	onEmailSent(emailId: ExchangeEmailId, response: EmailResponseSessionState | null): void;
	response: EmailResponseSessionState | null;
	totalVirtualSize: number;
	sizeLimit: number;
	onDeleteEmail(
		emailId: ExchangeEmailId,
		parentFolderId: string | null
	): void;
	onDraftSaved: () => void;
	sessionId: Guid | undefined;
	getPendingCommands(): readonly EmailSessionCommand[];
	onRemovePendingCommand(commandId: Guid): void;
	originalHead: string;
	doesRepresentNewlyGeneratedDraft: boolean;
	defaultComposerFontStyle: FontStyleModel;
	doesContainReplies: boolean;
};

type LoadedDraftHandle = {
	send(): void;
	cancel(): void;
	insertSignature(signature: string): void;
	attachFilesFromLocalDevice(): void;
	attachFilesFromDms(options: AttachFromDmsOptions): void;
	insertInlineImage(): void;
};

const LoadedDraft = forwardRef(
	(props: LoadedDraftProps, ref: Ref<LoadedDraftHandle>): ReactElement => {
		const {
			formData,
			onFormDataChange,
			attachments,
			onAddAttachment,
			onAttachmentUploadFinish,
			onRemoveAttachmentLocally,
			onDeleteAttachment,
			enqueueAttachmentUpload,
			fromAddressOptions,
			onIsBusyChange,
			onEmailSent,
			response,
			totalVirtualSize,
			sizeLimit,
			onDeleteEmail,
			sessionId,
			getPendingCommands,
			onRemovePendingCommand,
			originalHead,
			doesRepresentNewlyGeneratedDraft,
			defaultComposerFontStyle,
			onDraftSaved,
			doesContainReplies
		} = props;

		const [isCancelRequested, setIsCancelRequested] = useState<boolean>(false);
		const isSendingRef: RefObject<boolean> = useRef<boolean>(false);

		const alertManager: AlertManager = useAlerts();

		const setFormData: Dispatch<SetStateAction<EmailDraftFormData>> = useSetStateAction(
			formData,
			onFormDataChange
		);

		const formHandler: IFormHandler<EmailDraftFormData> = useFormHandler(
			formData,
			setFormData,
			validationConfiguration
		);

		const autoSaverRef: RefObject<DraftAutoSaver | null> = useRef<DraftAutoSaver>(null);
		const headerRef: RefObject<HeaderHandle | null> = useRef<HeaderHandle>(null);
		const bodyRef: RefObject<BodyHandle | null> = useRef<BodyHandle>(null);
		const totalVirtualSizeRef: ReadonlyRefObject<number> = useSynchronizedRef(totalVirtualSize);

		const defaultSignature: SignatureModel | undefined = useSignatureStore(x => x.signatures.find(s => s.isDefault));
		const defaultSignatureRef: ReadonlyRefObject<SignatureModel | undefined> = useSynchronizedRef(defaultSignature);

		const draftLocationQuery = useMemo(
			(): MailMessageLocationQueryModel => ({
				smtpAddress: convertOptionalModelToNullable(formData.smtpAddress),
				messageId: formData.exchangeId.id
			}),
			[
				formData.exchangeId.id,
				formData.smtpAddress
			]
		);

		const draftLocation = useMemo(
			(): MailMessageLocationModel => ({
				smtpAddress: formData.smtpAddress,
				messageId: formData.exchangeId.id
			}),
			[
				formData.exchangeId.id,
				formData.smtpAddress
			]
		);

		const {
			uploadAttachmentsFromDevice
		} = useUploadAttachmentsFromDevice({
			draft: draftLocationQuery,
			enqueueUpload: enqueueAttachmentUpload,
			onAddAttachment,
			onAttachmentUploadFinish,
			onRemoveAttachment: onRemoveAttachmentLocally,
			sizeLimit,
			totalVirtualSize
		});

		const {
			uploadAttachmentsFromDms
		} = useUploadAttachmentsFromDms({
			draft: draftLocation,
			enqueueUpload: enqueueAttachmentUpload,
			onAddAttachment,
			onAttachmentUploadFinish,
			onRemoveAttachment: onRemoveAttachmentLocally,
			sizeLimit,
			totalVirtualSize
		});

		const {
			uploadInlineImage
		} = useUploadInlineImage({
			draft: draftLocationQuery,
			enqueueUpload: enqueueAttachmentUpload,
			onAddAttachment,
			onAttachmentUploadFinish,
			onRemoveAttachment: onRemoveAttachmentLocally,
			sizeLimit
		});

		const preparedForSavingFormDataRef: ReadonlyRefObject<SendOrSaveEmailDraftModel> = useSynchronizedRef<SendOrSaveEmailDraftModel>(
			prepareFormData(
				formData,
				originalHead,
				doesRepresentNewlyGeneratedDraft,
				bodyRef.current
					?.getImmediateBodyHtml()
			)
		);

		useEffect(
			(): void => {
				hasImplicitDependencyOn(sessionId);
				setIsCancelRequested(false);
			},
			[sessionId]
		);

		useEffect(
			(): EffectWithDestructorResult => {
				hasImplicitDependencyOn(sessionId);

				const autoSaver = new DraftAutoSaver({
					intervalInSeconds: userConfiguration()
						.exchange
						.saveDraftsIntervalInSeconds,
					modelFactory: () => preparedForSavingFormDataRef.current,
					idChanged(id: string): void {
						setFormData(
							formData => {
								return {
									...formData,
									exchangeId: {
										...formData.exchangeId,
										id
									}
								};
							}
						);
					},
				});

				autoSaver.start();
				autoSaverRef.current = autoSaver;

				return (): void => {
					autoSaver.stop();
					autoSaverRef.current = null;
				};
			},
			[
				preparedForSavingFormDataRef,
				setFormData,
				sessionId
			]
		);

		useEffect(
			(): EffectWithDestructorResult => {
				const handle: SetTimeoutHandle = setTimeout(
					(): void => {
						const pendingCommands: readonly EmailSessionCommand[] = getPendingCommands();
						const {
							current: body
						} = bodyRef;

						for (const command of pendingCommands) {
							if (command.sessionId !== sessionId) {
								continue;
							}

							switch(command.kind) {
								case "insert-default-signature":
									const defaultSignatureContent: string | undefined = defaultSignatureRef.current
										?.content;

									if (isNotNilOrEmpty(defaultSignatureContent) && isNotNil(body)) {
										if (doesContainReplies) {
											body.placeCaretAtTheBeginning();
										} else {
											body.placeCaretAtTheEnd();
										}

										body.insertEmptyParagraph();
										body.insertSignature(defaultSignatureContent);
									}
									break;
								case "focus-to-field":
									headerRef.current
										?.focusToField();
									break;
								case "focus-body-field":
									if (isNotNil(body)) {
										body.placeCaretAtTheBeginning();
										body.focusEditor();
									}
									break;
								case "save-draft":
									autoSaverRef.current
										?.forceSave();
									break;
								case "apply-default-font-style":
									if (isNotNil(body)) {
										body.placeCaretAtTheBeginning();
										body.applyFontStyle(defaultComposerFontStyle);
									}
									break;
								default:
									never(command.kind);
							}

							onRemovePendingCommand(command.commandId);
						}
					},
					1
				);

				return (): void => clearTimeout(handle);
			},
			[
				defaultSignatureRef,
				getPendingCommands,
				onRemovePendingCommand,
				sessionId,
				defaultComposerFontStyle,
				doesContainReplies
			]
		);

		const sendHandler = useCallback(
			async (): Promise<void> => {
				if (isSendingRef.current) {
					return;
				}

				isSendingRef.current = true;

				const validationResult: InputValidationResult = await formHandler.validation
					.validate();

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					isSendingRef.current = false;

					const recipientsValidationResult: InputValidationResult = await formHandler.validation
						.validateStep(recipientsStep);

					if (recipientsValidationResult.kind === InputValidationResultKind.Invalid) {
						alertManager.showError(recipientsValidationResult.errorMessage);
					}
					return;
				}

				onIsBusyChange(true);

				autoSaverRef.current
					?.stop();

				try {
					await sendEmail(
						prepareFormData(
							formData,
							originalHead,
							doesRepresentNewlyGeneratedDraft
						)
					);
					onIsBusyChange(false);
				}
				catch (error) {
					isSendingRef.current = false;

					if (isCancelledError(error)) {
						return;
					}
					onIsBusyChange(false);

					autoSaverRef.current
						?.start();

					let handled: boolean = false;
					if (error instanceof HttpRequestError) {
						try {
							const { message } = error.readJson<IUserFriendlyExceptionMessage>();
							alertManager.showError(message);
							handled = true;
						} catch (error) {
							console.error(error);
						}
					}

					if (!handled) {
						alertManager.showError(localize("_EmailSendFailed"));
					}

					return;
				}

				isSendingRef.current = false;

				alertManager.showSuccess(localize("_EmailSent"));

				onEmailSent(formData.exchangeId, response);
			},
			[
				alertManager,
				formData,
				formHandler.validation,
				onEmailSent,
				onIsBusyChange,
				response,
				originalHead,
				doesRepresentNewlyGeneratedDraft
			]
		);

		const insertSignatureHandler = useCallback(
			(signature: string): void => {
				const {
					current: body
				} = bodyRef;

				if (!body) {
					return;
				}

				body.insertSignature(signature);
				body.focusEditor();
			},
			[]
		);

		const {
			open: openAttachFromDeviceDialog
		} = useFileSelector({
			isMultiple: true,
			onChange: uploadAttachmentsFromDevice
		});

		const [selectFileDialog, openSelectFileDialog] = useSelectFilesDialog();

		const openAttachFromDmsDialog = useCallback(
			async (options: AttachFromDmsOptions): Promise<void> => {
				const {
					isSharepointEnabled,
					webDavConfiguration
				} = options;

				const selectionResult: TryResult<FileSelection> = await openSelectFileDialog({
					options: {
						sharePointOptions: isSharepointEnabled
							? {
								isEnabled: true,
								shouldShowPersonalViews: true,
								availableDestination: { kind: "all" }
							}
							: { isEnabled: false },
						webDavOptions: webDavConfiguration && webDavConfiguration.isConfigured
							? {
								isEnabled: true,
								availableDestination: { kind: "all" }
							}
							: { isEnabled: false },
						commonOptions: {
							mode: ItemPickerMode.MultipleFiles,
							creationOptions: {
								folder: true
							}
						}
					}
				});

				if (selectionResult.wasSuccessful) {
					uploadAttachmentsFromDms(selectionResult.result);
				}
			},
			[
				openSelectFileDialog,
				uploadAttachmentsFromDms
			]
		);

		const insertInlineImageHandler = useCallback(
			async (file: File): Promise<void> => {
				const {
					current: body
				} = bodyRef;

				if (!body) {
					return;
				}

				const uploadedAttachment: UploadedInlineAttachment | undefined = await uploadInlineImage(
					file,
					totalVirtualSizeRef.current
				);

				if (isNil(uploadedAttachment)) {
					return;
				}

				body.insertInlineImage(uploadedAttachment);
				body.focusEditor();
			},
			[
				totalVirtualSizeRef,
				uploadInlineImage
			]
		);

		const {
			open: openInsertInlineImageDialog
		} = useFileSelector({
			isMultiple: false,
			contentTypes: ["image/*"],
			onChange: insertInlineImageHandler
		});

		const keepCurrentDraftHandler = useCallback(
			(): void => {
				const {
					current: autoSaver
				} = autoSaverRef;

				if (autoSaver) {
					autoSaver.stop();
					autoSaver.forceSave();
				}

				onDraftSaved();
			},
			[onDraftSaved]
		);

		const removeCurrentDraftHandler = useCallback(
			(): void => {
				const {
					current: autoSaver
				} = autoSaverRef;

				autoSaver?.stop();
				onDeleteEmail(
					formData.exchangeId,
					formData.parentFolderId?.id ?? null
				);
			},
			[
				formData.exchangeId,
				formData.parentFolderId,
				onDeleteEmail
			]
		);

		const continueComposingHandler = useCallback(
			(): void => {
				setIsCancelRequested(false);
			},
			[]
		);

		useImperativeHandle(
			ref,
			(): LoadedDraftHandle => ({
				send: sendHandler,
				cancel: () => setIsCancelRequested(true),
				insertSignature: insertSignatureHandler,
				attachFilesFromLocalDevice: openAttachFromDeviceDialog,
				attachFilesFromDms: openAttachFromDmsDialog,
				insertInlineImage: openInsertInlineImageDialog
			}),
			[
				sendHandler,
				insertSignatureHandler,
				openAttachFromDeviceDialog,
				openAttachFromDmsDialog,
				openInsertInlineImageDialog
			]
		);

		return (
			<div
				{...setTrackingId("email-composer")}
				className={styles.root}
			>
				<Header
					ref={headerRef}
					attachable={formHandler}
					fromAddressOptions={fromAddressOptions}
				/>
				<Body
					ref={bodyRef}
					bodyAttachable={formHandler.for("body")}
					attachments={attachments}
					draftLocation={draftLocation}
					onDeleteAttachment={onDeleteAttachment}
					onUploadFileAttachments={uploadAttachmentsFromDevice}
				/>
				{isCancelRequested && (
					<CancelEmailComposeDialog
						onKeep={keepCurrentDraftHandler}
						onRemove={removeCurrentDraftHandler}
						onCancel={continueComposingHandler}
					/>
				)}
				{selectFileDialog}
			</div>
		);
	}
);
LoadedDraft.displayName = "EmailComposer";

function convertOptionalModelToNullable(model: OptionalModel<string>): string | null {
	return model.isSet
		? model.value
		: null;
}

function convertNullableToOptionalModel(value: string | null): OptionalModel<string> {
	return isNotNil(value)
		? { isSet: true, value }
		: { isSet: false };
}

function convertToFormData(
	draftModel: NewDraftEmailModel | DraftEmailModel,
	body: string,
	defaultFromAddress: string,
	smtpAddress: string | null
): EmailDraftFormData {
	return {
		exchangeId: draftModel.exchangeId,
		smtpAddress: convertNullableToOptionalModel(smtpAddress),
		to: draftModel.to,
		cc: draftModel.cc,
		bcc: draftModel.bcc,
		fromAddress: draftModel.fromAddress ?? defaultFromAddress,
		body,
		importance: draftModel.importance ?? EmailImportance.Normal,
		subject: draftModel.subject ?? "",
		parentFolderId: draftModel.parentFolderId
	};
}

function getNewEmailModel(): NewDraftEmailModel {
	const model: NewDraftEmailModel = {
		exchangeId: { id: "" },
		to: "",
		cc: "",
		bcc: "",
		subject: "",
		bodyType: ExchangeMessageBodyKind.Html,
		body: "",
		size: 0,
		emailAttachments: [],
		fromAddress: null,
		parentFolderId: null
	};

	return model;
}

type AttachmentState = {
	items: readonly AttachmentItem[];
	totalSize: number;
};

type ComposeNewEmailData = {
	sessionId: Guid;
	to?: string | null;
	cc?: string | null;
	bcc?: string | null;
	smtpAddress: string | null;
};
type ComposeDraftData = {
	sessionId: Guid;
	draftId: ExchangeEmailId;
	smtpAddress: string | null;
	doesRepresentNewlyGeneratedDraft: boolean;
};

function useComposeNewEmail(
	sessionState: CreateNewEmailSessionState | EditDraftEmailSessionState,
	smtpAddress: string | null,
	defaultFromAddressRef: ReadonlyRefObject<string>,
	onFormDataChange: (newFormData: EmailDraftFormData) => void,
	onIsBusyChange: (isBusy: boolean) => void,
	onAttachmentStateChange: (state: AttachmentState) => void,
	onSessionIdChange: (sessionId: Guid) => void,
	onAddPendingCommand: (action: EmailSessionCommand) => void
): void {
	const unstableData = useMemo(
		(): ComposeNewEmailData | undefined => {
			if (sessionState.kind !== EmailSessionStateKind.NewEmail) {
				return undefined;
			}

			const {
				sessionId,
				to,
				cc,
				bcc
			} = sessionState;

			return {
				sessionId,
				to,
				cc,
				bcc,
				smtpAddress
			};
		},
		[sessionState, smtpAddress]
	);

	const data = useValueEqualMemo(
		(): ComposeNewEmailData | undefined => unstableData,
		[unstableData]
	);

	useEffect(
		(): void => {
			if (!data) {
				return;
			}

			onIsBusyChange(false);

			const {
				to,
				cc,
				bcc,
				sessionId,
				smtpAddress
			} = data;

			onSessionIdChange(sessionId);

			const draftModel: NewDraftEmailModel = getNewEmailModel();

			if (isNotNilOrEmpty(to)) {
				draftModel.to = to;
			}

			if (isNotNilOrEmpty(cc)) {
				draftModel.cc = cc;
			}

			if (isNotNilOrEmpty(bcc)) {
				draftModel.bcc = bcc;
			}

			onFormDataChange(
				convertToFormData(
					draftModel,
					draftModel.body ?? "",
					defaultFromAddressRef.current,
					smtpAddress
				)
			);

			onAttachmentStateChange(
				{
					items: draftModel.emailAttachments
						.map(i => convertToAttachmentItem(i)),
					totalSize: draftModel.size
				}
			);

			const addPendingCommandHandler = (kind: EmailSessionCommandKind): void => {
				onAddPendingCommand({
					kind,
					sessionId,
					commandId: newGuid()
				});
			};

			addPendingCommandHandler("insert-default-signature");
			addPendingCommandHandler("focus-to-field");
			addPendingCommandHandler("save-draft");
			addPendingCommandHandler("apply-default-font-style");
		},
		[
			data,
			defaultFromAddressRef,
			onFormDataChange,
			onAttachmentStateChange,
			onIsBusyChange,
			onSessionIdChange,
			onAddPendingCommand
		]
	);
}

function useEditDraft(
	sessionState: CreateNewEmailSessionState | EditDraftEmailSessionState,
	defaultFromAddressRef: ReadonlyRefObject<string>,
	onFormDataChange: (newFormData: EmailDraftFormData) => void,
	onIsBusyChange: (isBusy: boolean) => void,
	onAttachmentStateChange: (state: AttachmentState) => void,
	onSessionIdChange: (sessionId: Guid) => void,
	onAddPendingCommand: (action: EmailSessionCommand) => void,
	onOriginalHeadChange: (headValue: string) => void
): void {
	const alertManager: AlertManager = useAlerts();

	const unstableData = useMemo(
		(): ComposeDraftData | undefined => {
			if (sessionState.kind !== EmailSessionStateKind.EditDraft) {
				return undefined;
			}

			const {
				sessionId,
				emailId,
				smtpAddress,
				doesRepresentNewlyGeneratedDraft
			} = sessionState;

			return {
				sessionId,
				draftId: emailId,
				smtpAddress,
				doesRepresentNewlyGeneratedDraft
			};
		},
		[sessionState]
	);

	const data = useValueEqualMemo(
		(): ComposeDraftData | undefined => {
			return unstableData;
		},
		[unstableData]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			if (!data) {
				return;
			}

			async function fetchDraft(
				data: ComposeDraftData,
				signal: AbortSignal
			): Promise<void> {
				onIsBusyChange(true);

				try {
					const draftModel: DraftEmailModel = await getDraftById(
						{
							smtpAddress: convertNullableToOptionalModel(data.smtpAddress),
							messageId: data.draftId.id
						},
						signal
					);

					const addPendingCommandHandler = (kind: EmailSessionCommandKind): void => {
						onAddPendingCommand({
							kind,
							sessionId: data.sessionId,
							commandId: newGuid()
						});
					};

					if (data.doesRepresentNewlyGeneratedDraft) {
						addPendingCommandHandler("insert-default-signature");
					}

					if (isNotNilOrEmpty(draftModel.to)) {
						addPendingCommandHandler("focus-body-field");
					} else {
						addPendingCommandHandler("focus-to-field");
					}
					addPendingCommandHandler("apply-default-font-style");

					onIsBusyChange(false);

					let {
						head,
						body
					} = parseEmailContent(draftModel.body ?? "", draftModel.bodyType);

					if (data.doesRepresentNewlyGeneratedDraft) {
						body = "<p></p>" + body;
					}

					onFormDataChange(
						convertToFormData(
							draftModel,
							body,
							defaultFromAddressRef.current,
							data.smtpAddress
						)
					);
					onOriginalHeadChange(head);

					onSessionIdChange(data.sessionId);

					onAttachmentStateChange({
						items: draftModel.emailAttachments
							.map(i => convertToAttachmentItem(i)),
						totalSize: draftModel.size
					});
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onIsBusyChange(false);
				}
			}

			const cancellation = new AbortController();
			fetchDraft(
				data,
				cancellation.signal
			);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			defaultFromAddressRef,
			data,
			onAttachmentStateChange,
			onFormDataChange,
			onIsBusyChange,
			onSessionIdChange,
			onAddPendingCommand,
			onOriginalHeadChange
		]
	);
}

type EmailSessionCommandKind =
	| "focus-to-field"
	| "focus-body-field"
	| "insert-default-signature"
	| "save-draft"
	| "apply-default-font-style";

type EmailSessionCommand = {
	kind: EmailSessionCommandKind;
	commandId: Guid;
	sessionId: Guid;
};

const validationConfiguration: IValidationConfiguration<EmailDraftFormData> = createValidationConfiguration<EmailDraftFormData>();

const hasAtLeastOneRecipient: IValidationRuleWithBuilder<string> = inline(
	(value: string, validationContext: IValidationContext): InputValidationResult => {
		if (isNotNilOrWhiteSpace(value)) {
			return {
				kind: InputValidationResultKind.Valid
			};
		}

		const parent = validationContext.parentValue as EmailDraftFormData;

		if (
			isNotNilOrWhiteSpace(parent.to)
				|| isNotNilOrWhiteSpace(parent.cc)
				|| isNotNilOrWhiteSpace(parent.bcc)
		) {
			return {
				kind: InputValidationResultKind.Valid
			};
		}

		return {
			kind: InputValidationResultKind.Invalid,
			errorMessage: localize("_EmailRequired")
		};
	}
);

validationConfiguration.forScalar("to")
	.add(
		hasAtLeastOneRecipient
			.fixErrorOn("anyInput"),
		isValidRecipientValue
	);

validationConfiguration.forScalar("cc")
	.add(
		hasAtLeastOneRecipient
			.fixErrorOn("anyInput"),
		isValidRecipientValue
	);

validationConfiguration.forScalar("bcc")
	.add(
		hasAtLeastOneRecipient
			.fixErrorOn("anyInput"),
		isValidRecipientValue
	);

const recipientsStep: IObjectValidationStepBuilder<EmailDraftFormData> = configureValidationStep<EmailDraftFormData>()
	.add(
		"to",
		"cc",
		"bcc"
	);

const messageTemplate = (head: string, styles: string, body: string): string => {
	return `<html><head>${head}${styles}</head><body>${body}</body></html>`;
};

function parseEmailContent(
	emailBody: string,
	emailBodyType: ExchangeMessageBodyKind
): {
	head: string;
	body: string;
} {
	switch (emailBodyType) {
		case ExchangeMessageBodyKind.Text:
			return {
				head: "",
				body: convertPlainTextBodyToHtml(emailBody)
			};
		case ExchangeMessageBodyKind.Html:
			const parser = new DOMParser();
			const document: Document = parser.parseFromString(emailBody, "text/html");

			return {
				head: document.head.innerHTML,
				body: document.body.innerHTML
			};
		default:
			never(emailBodyType);
	}
}

function convertToSaveModel(formData: EmailDraftFormData): SendOrSaveEmailDraftModel {
	return {
		exchangeId: formData.exchangeId,
		smtpAddress: formData.smtpAddress,
		fromAddress: formData.fromAddress,
		to: formData.to,
		cc: formData.cc,
		bcc: formData.bcc,
		subject: formData.subject,
		body: formData.body,
		importance: formData.importance
	};
}

function prepareFormData(
	formData: EmailDraftFormData,
	originalHead: string,
	doesRepresentNewlyGeneratedDraft: boolean,
	customBodyHtml?: string
): SendOrSaveEmailDraftModel {
	return {
		...convertToSaveModel(formData),
		body: messageTemplate(
			originalHead,
			doesRepresentNewlyGeneratedDraft
				? DEFAULT_STYLE
				: "",
			customBodyHtml ?? formData.body
		)
	};
}

const DEFAULT_STYLE = `<style type="text/css">p { margin-top:0;margin-bottom:0;min-height:1em; }</style>`;

const COMPLETED_UPLOAD: Promise<void> = Promise.resolve();

type AttachFromDmsOptions = { isSharepointEnabled: boolean; webDavConfiguration: WebDavConfiguration | null; };